/**
 * Role-Based Access Control Configuration
 * Defines permissions and access levels for each role
 * 
 * PUBLIC SIGNUP:
 * - Only "nurse" role is available for public signups
 * 
 * INVITE-ONLY ROLES:
 * - recruiter, affiliate, admin, admin_regional require invite tokens
 * - Invite URL format: /onboarding/role?invite=recruiter
 * 
 * SUPERADMIN:
 * - Email whitelist only (no invite needed)
 */

export const SUPERADMINS = [
  "parfaitkiss@gmail.com",
  "founder@nursinghives.com"
]

export const ROLE_PERMISSIONS = {
  superadmin: {
    access: "all",
    canImpersonate: true,
    permissions: [
      "verify",
      "override",
      "reset_password",
      "view_audit",
      "view_payments",
      "manage_users",
      "manage_roles",
      "system_settings",
      "manage_admins"
    ]
  },
  admin: {
    access: ["/dashboard/admin", "/admin"],
    canImpersonate: false,
    permissions: [
      "view_nurses",
      "verify_nurses",
      "manage_nurse_profiles",
      "view_access_requests",
      "approve_access_requests",
      "create_invites",
      "send_messages",
      "view_messages",
      "view_email_analytics"
    ]
  },
  recruiter: {
    access: ["/dashboard/recruiter"],
    permissions: [
      "view_nurses",
      "post_jobs",
      "manage_applications",
      "view_analytics"
    ]
  },
  affiliate: {
    access: ["/dashboard/affiliate"],
    permissions: [
      "view_referrals",
      "track_commissions",
      "generate_links"
    ]
  },
  nurse: {
    access: ["/dashboard/nurse"],
    permissions: [
      "manage_profile",
      "upload_documents",
      "view_jobs",
      "apply_jobs"
    ]
  }
}

export const ROLE_DASHBOARDS = {
  nurse: "/dashboard/nurse",
  recruiter: "/dashboard/recruiter",
  affiliate: "/dashboard/affiliate",
  admin: "/dashboard/admin",
  superadmin: "/dashboard/superadmin"
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(role: string, permission: string): boolean {
  const rolePerms = ROLE_PERMISSIONS[role as keyof typeof ROLE_PERMISSIONS]
  if (!rolePerms) return false
  return rolePerms.permissions.includes(permission)
}

/**
 * Check if user can access a specific path
 */
export function canAccessPath(role: string, path: string): boolean {
  const rolePerms = ROLE_PERMISSIONS[role as keyof typeof ROLE_PERMISSIONS]
  if (!rolePerms) return false
  
  if (rolePerms.access === "all") return true
  if (Array.isArray(rolePerms.access)) {
    return rolePerms.access.some(allowedPath => path.startsWith(allowedPath))
  }
  return false
}

/**
 * Check if email is in superadmin whitelist
 * Case-insensitive comparison
 */
export function isSuperadmin(email: string): boolean {
  if (!email) return false
  const normalizedEmail = email.toLowerCase().trim()
  console.log("isSuperadmin check - Input:", email, "Normalized:", normalizedEmail, "Result:", SUPERADMINS.includes(normalizedEmail))
  return SUPERADMINS.includes(normalizedEmail)
}

/**
 * Check if email domain is authorized for admin role
 */
export function isAdminDomain(email: string): boolean {
  const domain = email.split('@')[1]
  return domain === 'nursinghives.com' || domain === 'nursinghive.com'
}
